import React, { useState, useRef, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { Autocomplete, Box, TextField, Tooltip, IconButton } from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Modal from 'components/Modal'
import Button from 'components/Button'
import Table from 'components/Table'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { v4 as uuid } from 'uuid'
import { selectActiveStudents } from 'modules/students/ducks/selectors'
import { useSelector } from 'react-redux'
import { parseCsvToOperations } from 'utils/paymentsFile'
import { addStudentPayment, getLastStudentDebitItems } from 'services/StudentsService'
import { addPaymentProblems } from 'services/StudentPaymentProblemsService'
import { dateWithTime } from 'utils/date'
import { propOr } from 'ramda'
import { parseNumber } from 'utils/numbers'
import { STUDENT_DEBIT_ITEM_TYPES } from 'utils/student'
import { DATE_FORMATS, formatDate } from 'utils/date'
import moment from 'moment'

const parseAmountFromInput = str => {
  if (str == null || String(str).trim() === '') return NaN
  return parseFloat(String(str).replace(/\s/g, '').replace(',', '.'))
}

/** Wartość dla input[type=date] (YYYY-MM-DD) z tekstu z CSV / bazy. */
const parseToDateInputValue = raw => {
  if (raw == null || String(raw).trim() === '') return ''
  const strict = moment(
    raw,
    [
      DATE_FORMATS.input,
      'DD-MM-YYYY',
      'DD.MM.YYYY',
      'D.M.YYYY',
      'DD/MM/YYYY',
      'YYYY/MM/DD'
    ],
    true
  )
  if (strict.isValid()) return strict.format(DATE_FORMATS.input)
  const loose = moment(raw)
  return loose.isValid() ? loose.format(DATE_FORMATS.input) : ''
}

const normalizeStudentName = name => {
  return name
  .toLowerCase()
  .replace(/ą/g, 'a')
  .replace(/ć/g, 'c')
  .replace(/ę/g, 'e')
  .replace(/ł/g, 'l')
  .replace(/ń/g, 'n')
  .replace(/ó/g, 'o')
  .replace(/ś/g, 's')
  .replace(/ź/g, 'z')
  .replace(/ż/g, 'z')
  .replace(/\(.*\)/g, '')
  .trim()
}

const splitNameWords = str => {
  if (!str || typeof str !== 'string') return []
  return str.trim().split(/\s+/).filter(Boolean)
}

/**
 * 1) Pierwsze dwa słowa z tytułu vs pierwsze dwa słowa ucznia (np. pełne imię+nazwisko).
 * 2) Jeśli brak trafienia — drugie słowo z tytułu vs drugie słowo ucznia (np. Ala vs Alicja, to samo nazwisko).
 * 3) Pełne dopasowanie całego tytułu do całej nazwy ucznia (jak wcześniej).
 */
const findStudentByBeneficiaryName = (students, beneficiaryName) => {
  const titleWords = splitNameWords(beneficiaryName)
  if (titleWords.length === 0) return null

  if (titleWords.length >= 2) {
    const titleFirstTwo = normalizeStudentName(`${titleWords[0]} ${titleWords[1]}`)
    const byFirstTwo = students.find(student => {
      const sw = splitNameWords(student.name)
      if (sw.length < 2) return false
      return normalizeStudentName(`${sw[0]} ${sw[1]}`) === titleFirstTwo
    })
    if (byFirstTwo) return byFirstTwo

    const titleSecond = normalizeStudentName(titleWords[1])
    const bySecondWord = students.find(student => {
      const sw = splitNameWords(student.name)
      if (sw.length < 2) return false
      return normalizeStudentName(sw[1]) === titleSecond
    })
    if (bySecondWord) return bySecondWord
  }

  const normalizedBeneficiaryName = normalizeStudentName(beneficiaryName)
  return students.find(student => normalizeStudentName(student.name) === normalizedBeneficiaryName)
}

const debitHistoryCache = {}

const dedupeDebitItemsById = items => {
  const seen = new Set()
  return (items || []).filter(item => {
    const k = item?.id != null ? String(item.id) : null
    if (!k || seen.has(k)) return false
    seen.add(k)
    return true
  })
}

const tooltipPanelSx = {
  bgcolor: '#2d2d2d',
  color: '#f0f0f0',
  p: '10px 12px',
  borderRadius: '8px',
  width: 400,
  maxWidth: 400,
  boxSizing: 'border-box',
  boxShadow: '0 6px 24px rgba(0, 0, 0, 0.35)',
  textAlign: 'left',
  fontSize: 11,
  lineHeight: 1.4
}

const DebitHistoryTooltipBody = ({ loading, error, items }) => {
  if (loading) return <TooltipMessage>Wczytywanie historii…</TooltipMessage>
  if (error) return <TooltipMessage>Nie udało się wczytać historii salda.</TooltipMessage>
  if (!items?.length) return <TooltipMessage>Brak wpisów w historii salda.</TooltipMessage>
  return (
    <>
      <TooltipHeading>Ostatnie 10 wpisów salda</TooltipHeading>
      <TooltipHeader>
        <span>Typ</span>
        <span>Data</span>
        <span>Zm.</span>
        <span>Saldo</span>
      </TooltipHeader>
      <TooltipList>
        {dedupeDebitItemsById(items).map(item => (
          <TooltipLine key={item.id}>
            <TooltipCell $flex={1.1}>{STUDENT_DEBIT_ITEM_TYPES[item.type] ?? item.type ?? '—'}</TooltipCell>
            <TooltipCell $w={76}>{formatDate(item.date, DATE_FORMATS.dashed)}</TooltipCell>
            <TooltipCell $w={44}>
              <TooltipValue $positive={parseNumber(item.value) >= 0}>
                {item.value != null && item.value !== '' ? String(item.value) : '—'}
              </TooltipValue>
            </TooltipCell>
            <TooltipCell $flex={0.9}>{item.balanceAfter != null && item.balanceAfter !== '' ? String(item.balanceAfter) : '—'}</TooltipCell>
          </TooltipLine>
        ))}
      </TooltipList>
    </>
  )
}

const StudentSelectWithDebitTooltip = ({ student, students, onStudentChange }) => {
  const [tooltipState, setTooltipState] = useState({ items: null, loading: false, error: false })
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const [listboxOpen, setListboxOpen] = useState(false)
  /**
   * Po zamknięciu listy: jeśli kursor nadal jest nad selectem, tooltip tylko po wyjściu myszy ze strefy i ponownym najechaniu.
   * Jeśli kursor już poza selectem (np. wybór z listy w portalu), kolejne najechanie od razu może pokazać tooltip.
   */
  const needLeaveBeforeTooltipRef = useRef(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    setTooltipState({ items: null, loading: false, error: false })
    setTooltipOpen(false)
    setListboxOpen(false)
    needLeaveBeforeTooltipRef.current = false
  }, [student?.id])

  const loadDebitHistoryOnTooltipOpen = async () => {
    const sid = student?.id
    if (!sid) {
      setTooltipState({ items: null, loading: false, error: false })
      return
    }
    if (debitHistoryCache[sid]) {
      setTooltipState({ items: debitHistoryCache[sid], loading: false, error: false })
      return
    }
    setTooltipState({ items: null, loading: true, error: false })
    try {
      const items = await getLastStudentDebitItems({ id: sid, limit: 10 })
      debitHistoryCache[sid] = items
      setTooltipState({ items, loading: false, error: false })
    } catch {
      setTooltipState({ items: null, loading: false, error: true })
    }
  }

  const titleInner =
    student?.id != null ? (
      <DebitHistoryTooltipBody {...tooltipState} />
    ) : (
      <TooltipMessage>Wybierz ucznia, aby zobaczyć ostatnie wpisy salda.</TooltipMessage>
    )

  const title = <Box sx={tooltipPanelSx}>{titleInner}</Box>

  const handleWrapperMouseEnter = () => {
    if (listboxOpen) return
    if (needLeaveBeforeTooltipRef.current) return
    setTooltipOpen(true)
    loadDebitHistoryOnTooltipOpen()
  }

  const handleWrapperMouseLeave = () => {
    setTooltipOpen(false)
    needLeaveBeforeTooltipRef.current = false
  }

  const handleAutocompleteOpen = () => {
    setListboxOpen(true)
    setTooltipOpen(false)
  }

  const handleAutocompleteClose = () => {
    setListboxOpen(false)
    requestAnimationFrame(() => {
      const el = wrapRef.current
      needLeaveBeforeTooltipRef.current = Boolean(el?.matches?.(':hover'))
    })
  }

  return (
    <Tooltip
      title={title}
      open={Boolean(tooltipOpen && !listboxOpen)}
      placement='left'
      disableInteractive
      disableHoverListener
      disableFocusListener
      disableTouchListener
      PopperProps={{
        disablePortal: false,
        style: { zIndex: 20000 },
        sx: {
          '& .MuiTooltip-tooltip': {
            bgcolor: 'transparent',
            color: 'inherit',
            p: 0,
            m: 0,
            maxWidth: 'none',
            boxShadow: 'none',
            border: 'none'
          }
        }
      }}
    >
      <span
        ref={wrapRef}
        style={{ display: 'inline-block', minWidth: 200, maxWidth: 260 }}
        onMouseEnter={handleWrapperMouseEnter}
        onMouseLeave={handleWrapperMouseLeave}
      >
        <CompactStudentAutocomplete
          size='small'
          options={students}
          value={student ?? null}
          onChange={(e, newValue) => onStudentChange(newValue)}
          onOpen={handleAutocompleteOpen}
          onClose={handleAutocompleteClose}
          getOptionLabel={opt => opt?.name ?? ''}
          isOptionEqualToValue={(a, b) => a?.id === b?.id}
          /** Bez unikalnego key React/MUI łączy opcje o tej samej nazwie — lista i tooltip wyglądają na „powielone”. */
          renderOption={(props, option) => {
            const { key: _muiKey, ...liProps } = props
            const hint =
              option?.email ||
              option?.phoneNumber ||
              (option?.id != null ? String(option.id).slice(0, 8) : '')
            return (
              <li {...liProps} key={option.id}>
                <AutocompleteOptionInner>
                  <span>{option?.name ?? ''}</span>
                  {hint ? <AutocompleteOptionHint>{hint}</AutocompleteOptionHint> : null}
                </AutocompleteOptionInner>
              </li>
            )
          }}
          noOptionsText='Brak wyników'
          clearOnEscape
          ListboxProps={{ style: { maxHeight: 280, fontSize: 12 } }}
          componentsProps={{
            popper: { style: { zIndex: 20000 } }
          }}
          renderInput={params => (
            <TextField
              {...params}
              variant='outlined'
              placeholder='Szukaj ucznia…'
              InputProps={{
                ...params.InputProps,
                sx: { fontSize: 12, '& input': { py: 0.5 } }
              }}
            />
          )}
          sx={{
            minWidth: 200,
            maxWidth: 260,
            width: '100%',
            '& .MuiOutlinedInput-root': { backgroundColor: '#fff' }
          }}
        />
      </span>
    </Tooltip>
  )
}

const PaymentsFromFileModal = ({ open, onClose }) => {
  const fileInputRef = useRef(null)
  const [step, setStep] = useState(1)
  const [fileName, setFileName] = useState('')
  const [csvContent, setCsvContent] = useState('')
  const [isDragging, setIsDragging] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const studentsRaw = useSelector(selectActiveStudents)

  const students = useMemo(() => {
    const seen = new Set()
    return studentsRaw.filter(s => {
      if (seen.has(s.id)) return false
      seen.add(s.id)
      return true
    })
  }, [studentsRaw])

  const { allOperations, total } = useMemo(() => {
    const { operations, total: csvTotal } = parseCsvToOperations(csvContent)
    return {
      allOperations: operations.map(op => ({
        amount: op.amount,
        date: op.date,
        displayTitle: op.title,
        title: op.extracted ? op.title : null,
        rawTitle: op.extracted ? undefined : op.title,
        extracted: op.extracted,
        student: op.extracted ? findStudentByBeneficiaryName(students, op.title) ?? null : null
      })),
      total: csvTotal
    }
  }, [csvContent, students])

  const [selectedStudentOverrides, setSelectedStudentOverrides] = useState({})
  const [rowEdits, setRowEdits] = useState({})
  const [removedRowIndices, setRemovedRowIndices] = useState(() => new Set())
  const [bulkDate, setBulkDate] = useState('')
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0, problemsCount: 0, done: false, error: null })

  const operationsWithStudents = useMemo(() => {
    return allOperations.map((op, index) => ({
      ...op,
      // Ręczny wybór ma pierwszeństwo — auto-dopasowanie daje truthy op.student, więc ?? ignorowało override.
      student:
        selectedStudentOverrides[index] !== undefined
          ? selectedStudentOverrides[index]
          : op.student
    }))
  }, [allOperations, selectedStudentOverrides])

  const mergedOperations = useMemo(() => {
    return operationsWithStudents.map((op, index) => {
      const e = rowEdits[index]
      let amount = op.amount
      if (e?.amountStr !== undefined) {
        const parsed = parseAmountFromInput(e.amountStr)
        if (!Number.isNaN(parsed)) amount = parsed
      }
      const date =
        e?.date !== undefined && e.date !== ''
          ? e.date
          : op.date

      return {
        ...op,
        date,
        amount
      }
    })
  }, [operationsWithStudents, rowEdits])

  const mergedOperationsActive = useMemo(() => {
    return mergedOperations.filter((_, index) => !removedRowIndices.has(index))
  }, [mergedOperations, removedRowIndices])

  const activeRowCount = allOperations.length - removedRowIndices.size
  const canSave = activeRowCount > 0

  const resetState = () => {
    setStep(1)
    setFileName('')
    setCsvContent('')
    setIsProcessing(false)
    setSelectedStudentOverrides({})
    setRowEdits({})
    setRemovedRowIndices(new Set())
    setBulkDate('')
    setSaveProgress({ current: 0, total: 0, problemsCount: 0, done: false, error: null })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  useEffect(() => {
    if (open) resetState()
  }, [open])

  useEffect(() => {
    if (!open) {
      Object.keys(debitHistoryCache).forEach(k => {
        delete debitHistoryCache[k]
      })
    }
  }, [open])

  useEffect(() => {
    setRowEdits({})
    setBulkDate('')
    setRemovedRowIndices(new Set())
  }, [csvContent])

  const processFile = file => {
    setIsProcessing(true)
    const reader = new FileReader()
    reader.onload = ev => {
      const content = ev.target.result
      setCsvContent(content)
      setIsProcessing(false)
    }
    reader.readAsText(file, 'UTF-8')
    setFileName(file.name)
  }

  const handleFileChange = e => {
    const file = e.target.files?.[0]
    if (!file || !file.name.toLowerCase().endsWith('.csv')) return
    processFile(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDrop = e => {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer?.files?.[0]
    if (!file || !file.name.toLowerCase().endsWith('.csv')) return
    processFile(file)
  }

  const handleDragOver = e => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => setIsDragging(false)

  const handleZoneClick = () => fileInputRef.current?.click()

  const handleNext = () => setStep(2)
  const handleBack = () => setStep(1)

  const applyBulkDateToAllRows = () => {
    if (!bulkDate) return
    setRowEdits(prev => {
      const next = { ...prev }
      operationsWithStudents.forEach((_, i) => {
        if (removedRowIndices.has(i)) return
        next[i] = { ...next[i], date: bulkDate }
      })
      return next
    })
  }

  const handleRemoveRow = index => {
    setRemovedRowIndices(prev => {
      const next = new Set(prev)
      next.add(index)
      return next
    })
  }

  const handleSave = async () => {
    if (!canSave) return
    const opsWithStudent = mergedOperationsActive.filter(op => op.student != null)
    const opsWithoutStudent = mergedOperationsActive.filter(op => op.student == null)
    const sortedForPayment = [...opsWithStudent].sort((a, b) => new Date(a.date) - new Date(b.date))

    setStep(3)
    setSaveProgress({ current: 0, total: sortedForPayment.length, done: false, error: null })

    const delay = ms => new Promise(resolve => setTimeout(resolve, ms))
    const balancesByStudent = {}

    try {
      for (let i = 0; i < sortedForPayment.length; i++) {
        const op = sortedForPayment[i]
        const student = op.student
        const currentBalance = balancesByStudent[student.id] ?? parseNumber(propOr(0, 'balance', student))
        const newBalance = currentBalance + parseNumber(op.amount)
        balancesByStudent[student.id] = newBalance

        const debitId = uuid()
        const createdAt = dateWithTime(new Date())

        const debitPayload = {
          type: 'payment',
          studentId: student.id,
          comment: '',
          createdAt,
          id: debitId,
          value: parseNumber(op.amount),
          date: dateWithTime(op.date),
          balanceAfter: newBalance
        }

        const studentPayload = {
          ...student,
          balance: newBalance
        }

        await addStudentPayment({
          values: { debitPayload, studentPayload },
          suppressToast: true
        })

        setSaveProgress(prev => ({ ...prev, current: i + 1 }))

        if (i < sortedForPayment.length - 1) {
          await delay(1000)
        }
      }

      if (opsWithoutStudent.length > 0) {
        await addPaymentProblems(
          opsWithoutStudent.map(op => ({
            date: op.date,
            amount: op.amount,
            title: op.displayTitle || op.rawTitle || op.title || '',
            extractedName: op.title || null
          }))
        )
      }

      setSaveProgress(prev => ({ ...prev, done: true, problemsCount: opsWithoutStudent.length }))
    } catch (err) {
      setSaveProgress(prev => ({ ...prev, done: true, error: err?.message || 'Wystąpił błąd' }))
    }
  }

  const canProceed = !!fileName && !!csvContent && !isProcessing

  return (
    <Modal open={open} onClose={onClose} title='Wpłaty z pliku' disableBackdropClose>
      <Content>
        {step === 1 && (
          <FileDropZone
            isDragging={isDragging}
            onClick={handleZoneClick}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <HiddenInput
              ref={fileInputRef}
              type='file'
              accept='.csv,text/csv'
              onChange={handleFileChange}
            />
            <UploadIcon />
            <ZoneText>
              {fileName
                ? `Wybrano: ${fileName}`
                : 'Przeciągnij plik CSV tutaj lub kliknij, aby wybrać'}
            </ZoneText>
            <ZoneHint>Plik CSV przygotowany w Narzędziach</ZoneHint>
          </FileDropZone>
        )}

        {step === 3 && (
          <ProgressSection>
            {!saveProgress.done ? (
              <>
                <ProgressText>Zapisywanie wpłat…</ProgressText>
                <ProgressCount>
                  {saveProgress.current} / {saveProgress.total}
                </ProgressCount>
              </>
            ) : saveProgress.error ? (
              <ProgressError>{saveProgress.error}</ProgressError>
            ) : saveProgress.total > 0 || saveProgress.problemsCount > 0 ? (
              <ProgressSuccess>
                {saveProgress.total > 0 && `Zapisano ${saveProgress.total} wpłat.`}
                {saveProgress.problemsCount > 0 && ` ${saveProgress.problemsCount} wpisów dodano do problemów.`}
              </ProgressSuccess>
            ) : (
              <ProgressSuccess>Brak wpłat do zapisania (żaden wiersz nie ma wybranego ucznia).</ProgressSuccess>
            )}
          </ProgressSection>
        )}

        {step === 2 && (
          <DataPreview>
            {total != null && (
              <TotalSummary>
                Łącznie z zestawienia: <strong>{total.toFixed(2)} zł</strong>
              </TotalSummary>
            )}
            <PreviewTitle>Dopasowanie wpłat do uczniów</PreviewTitle>
            <BulkDateToolbar onClick={e => e.stopPropagation()}>
              <BulkDateField
                type='date'
                label='Data dla wszystkich wpisów'
                size='small'
                value={bulkDate}
                onChange={e => setBulkDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <DateChangeButton type='button' color='primary-outline' onClick={applyBulkDateToAllRows} disabled={!bulkDate}>
                Ustaw we wszystkich wierszach
              </DateChangeButton>
            </BulkDateToolbar>
            <Table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Kwota</th>
                  <th>Tytuł</th>
                  <th>Uczeń</th>
                  <th aria-label='Usuń wiersz' />
                </tr>
              </thead>
              <tbody>
                {operationsWithStudents.map((op, index) => {
                  if (removedRowIndices.has(index)) return null
                  const dateEdit = rowEdits[index]?.date
                  const dateValue =
                    dateEdit !== undefined ? dateEdit : parseToDateInputValue(op.date)
                  const amountValue =
                    rowEdits[index]?.amountStr !== undefined
                      ? rowEdits[index].amountStr
                      : (op.amount != null ? op.amount.toFixed(2) : '')
                  return (
                    <tr key={index}>
                      <td onClick={e => e.stopPropagation()}>
                        <TableDateField
                          type='date'
                          size='small'
                          value={dateValue}
                          onChange={e =>
                            setRowEdits(prev => ({
                              ...prev,
                              [index]: { ...prev[index], date: e.target.value }
                            }))
                          }
                          InputLabelProps={{ shrink: true }}
                          variant='outlined'
                        />
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <TableAmountField
                          size='small'
                          value={amountValue}
                          onChange={e =>
                            setRowEdits(prev => ({
                              ...prev,
                              [index]: { ...prev[index], amountStr: e.target.value }
                            }))
                          }
                          placeholder='0.00'
                          variant='outlined'
                          inputProps={{ inputMode: 'decimal' }}
                        />
                      </td>
                      <td>{op.displayTitle}</td>
                      <td>
                        <StudentSelectWithDebitTooltip
                          student={op.student}
                          students={students}
                          onStudentChange={st =>
                            setSelectedStudentOverrides(prev => ({ ...prev, [index]: st }))
                          }
                        />
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <RemoveRowIconButton
                          size='small'
                          aria-label='Usuń wiersz z importu'
                          onClick={() => handleRemoveRow(index)}
                          title='Usuń wiersz (nie zostanie zapisany)'
                        >
                          <DeleteOutlineIcon fontSize='small' />
                        </RemoveRowIconButton>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </DataPreview>
        )}
      </Content>
      <ButtonsWrapper>
        {step === 1 && (
          <Button
            color='primary'
            onClick={handleNext}
            disabled={!canProceed}
          >
            {isProcessing ? 'Wczytywanie...' : 'Dalej'}
          </Button>
        )}
        {step === 2 && (
          <>
            <Button color='secondary' onClick={handleBack}>
              Wstecz
            </Button>
            <Button color='primary' onClick={handleSave} disabled={!canSave}>
              Zapisz
            </Button>
          </>
        )}
        {step === 3 && saveProgress.done && (
          <Button color='primary' onClick={onClose}>
            Zamknij
          </Button>
        )}
      </ButtonsWrapper>
    </Modal>
  )
}

export default PaymentsFromFileModal

const Content = styled.div`
  padding: 20px 0;
`

const FileDropZone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px 24px;
  border: 2px dashed ${({ isDragging, theme }) =>
    isDragging ? theme.colors.primary.main : (theme.colors.border || '#ddd')};
  border-radius: 8px;
  background-color: ${({ isDragging, theme }) =>
    isDragging ? (theme.colors.primary?.light || '#e3f2fd') + '40' : (theme.colors.lightGrey || '#f5f5f5') + '60'};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary?.main || '#1976d2'};
    background-color: ${({ theme }) => (theme.colors.primary?.light || '#e3f2fd') + '30'};
  }
`

const HiddenInput = styled.input`
  display: none;
`

const UploadIcon = styled(UploadFileOutlinedIcon)`
  font-size: 48px !important;
  color: ${({ theme }) => theme.colors.secondary?.main || '#9c27b0'};
  opacity: 0.85;
`

const ZoneText = styled.span`
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text || '#333'};
  text-align: center;
`

const ZoneHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.darkGrey || '#666'};
`

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.border || '#eee'};
`

const DataPreview = styled.div`
  padding: 0;
  max-height: 400px;
  overflow-y: auto;
`

const TotalSummary = styled.div`
  font-size: 14px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: ${({ theme }) => (theme.colors.primary?.light || '#e3f2fd') + '40'};
  border-radius: 6px;
  color: ${({ theme }) => theme.colors.text || '#333'};
`

const ProgressSection = styled.div`
  padding: 32px 24px;
  text-align: center;
`

const ProgressText = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text || '#333'};
  margin-bottom: 8px;
`

const ProgressCount = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary?.main || '#1976d2'};
`

const ProgressSuccess = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.success || '#2e7d32'};
  font-weight: 500;
`

const ProgressError = styled.div`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.error || '#d32f2f'};
`

const PreviewTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.secondary?.main || '#9c27b0'};
  text-transform: uppercase;
`


const CompactStudentAutocomplete = styled(Autocomplete)`
  .MuiInputBase-root {
    background-color: #fff;
  }
`

const AutocompleteOptionInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  line-height: 1.25;
`

const AutocompleteOptionHint = styled.span`
  font-size: 10px;
  color: rgba(0, 0, 0, 0.45);
  word-break: break-all;
`

const BulkDateToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
  margin-top: 20px;
`

const BulkDateField = styled(TextField)`
  min-width: 220px;

  .MuiInputBase-root {
    background-color: #fff;
  }
`

const DateChangeButton = styled(Button)`
  max-width: 300px;
`

const RemoveRowIconButton = styled(IconButton)`
  color: ${({ theme }) => theme.colors.darkGrey};

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`

const TableDateField = styled(TextField)`
  min-width: 152px;
  max-width: 168px;

  .MuiInputBase-root {
    background-color: #fff;
    font-size: 12px;
  }
`

const TableAmountField = styled(TextField)`
  width: 92px;
  max-width: 100px;

  .MuiInputBase-root {
    background-color: #fff;
    font-size: 12px;
  }
`

const TooltipMessage = styled.p`
  margin: 0;
  font-size: 11px;
  line-height: 1.4;
  color: #f0f0f0;
`

const TooltipHeading = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 13px;
  color: #fff;
`

const TooltipHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.22);

  & > span:nth-child(1) {
    flex: 1.1;
    min-width: 0;
  }
  & > span:nth-child(2) {
    width: 76px;
    flex-shrink: 0;
  }
  & > span:nth-child(3) {
    width: 44px;
    flex-shrink: 0;
    text-align: right;
  }
  & > span:nth-child(4) {
    flex: 0.9;
    min-width: 0;
    text-align: right;
  }
`

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const TooltipLine = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;
  color: #e8e8e8;
`

const TooltipCell = styled.span`
  ${({ $w }) => ($w != null ? `width: ${$w}px; flex-shrink: 0;` : '')}
  ${({ $flex }) => ($flex != null ? `flex: ${$flex}; min-width: 0;` : '')}
  &:nth-child(3) {
    text-align: right;
  }
  &:nth-child(4) {
    text-align: right;
  }
`

const TooltipValue = styled.span`
  color: ${({ $positive }) => ($positive ? '#81c784' : '#e57373')};
  font-weight: 600;
`
